import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import GenericTable from "@components/tables/GenericTable";
import { setSelectedAssignedFileId, setSelectedFileTargetLanguageId, setDevAdminId, setSourceId, setTargetId } from '@store/slices/devnagriValidatorAdminSlice';
import { fetchValidatorsByLanguage, assignValidatorToFile, fetchInProgressFiles, fetchSentencesByFileIdDA, sendToCDAC } from "@store/thunks/devnagriValidationAdminThunk";
import { BASE_URL, getStatusStyle, restrictSpecialCharacters } from '@utils/constants';
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { setFileMeta } from "@/store/slices/fileMetaSlice";
import { setFileAndLanguage } from "@/store/slices/fileLanguageSlice";

const FileDetailTableDevnagriValidatorAdmin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loadingValidators, setLoadingValidators] = useState(false);
  const [assignedValidators, setAssignedValidators] = useState({});
  const [selectedValidator, setSelectedValidator] = useState({});
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assigningFileId, setAssigningFileId] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [refreshingFiles, setRefreshingFiles] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [popup, setPopup] = useState({ isOpen: false, type: "", message: "" });

  const {
    selectedAssignedFolderId,
    assignedFiles,
    validatorsByLanguage,
    selectedFileTargetLanguageId,
    targetlanguageId
  } = useSelector((store) => store.devnagriValidatorAdmin);

  // Get files for the selected folder
  const currentFiles = selectedAssignedFolderId ? (assignedFiles[selectedAssignedFolderId] || []) : [];



  // Fetch files when folder changes
  useEffect(() => {
    const fetchFiles = async () => {
      if (!selectedAssignedFolderId) {
        setInitialLoading(false);
        return;
      }
      try {
        setInitialLoading(true);
        const res = await dispatch(
          fetchInProgressFiles(
            selectedAssignedFolderId,
            searchQuery,
            pageIndex,
            pageSize
          )
        );

        if (Array.isArray(res)) {
          if (res.length < pageSize) {
            setHasNextPage(false);
          } else {
            setHasNextPage(true);
          }
        }
        setInitialLoading(false);
      } catch (error) {
        console.error("Error fetching files:", error);
        setInitialLoading(false);
      }
    };
    fetchFiles();
  }, [dispatch, selectedAssignedFolderId, pageIndex, pageSize]);

  // Store targetLanguageId in Redux when files are loaded
  useEffect(() => {
    if (currentFiles.length > 0) {
      const firstFile = currentFiles[0];
      console.log("First file data:", firstFile);
      if (firstFile && firstFile.targetId) {
        console.log("Setting selectedFileTargetLanguageId to:", firstFile.targetId);
        dispatch(setSelectedFileTargetLanguageId(firstFile.targetId));
      }
    }
  }, [currentFiles, dispatch]);

  const handleAssignButtonClick = async (row) => {
    console.log(row);
    const { fileId, targetId } = row.original;
    try {
      setLoadingValidators(true);
      setAssigningFileId(fileId);
      console.log(targetId);
      setTargetId(targetId);

      const file = currentFiles.find(f => f.fileId === fileId && f.targetId === targetId);
      console.log("File found:", file);
      if (file && file.targetId) {
        console.log("Fetching validators for targetId:", targetId);
        const existingValidators = validatorsByLanguage[targetId];
        if (!existingValidators || existingValidators.length === 0) {
          await dispatch(fetchValidatorsByLanguage(targetId));
        }
        dispatch(setSelectedFileTargetLanguageId(targetId));
      } else {
        console.error("File not found or no targetId:", file);
      }
      setAssignModalOpen(true);
    } catch (error) {
      console.error("Error fetching validators:", error);
    } finally {
      setLoadingValidators(false);
    }
  };

  const handleValidatorOptionClick = (userId) => {
    setSelectedValidator((prev) => ({ ...prev, [assigningFileId]: userId }));
    setShowDropdown(false);
  };

  const closeModal = () => {
    console.log("Closing modal and resetting states");
    console.log("Before closing - assignModalOpen:", assignModalOpen, "assigningFileId:", assigningFileId);
    setAssignModalOpen(false);
    setShowDropdown(false);
    setSelectedValidator((prev) => {
      const newState = { ...prev };
      delete newState[assigningFileId];
      return newState;
    });
    setAssigningFileId(null);
    console.log("After closing - assignModalOpen should be false");
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    if (restrictSpecialCharacters(newValue) || newValue === "") {
      setInputValue(newValue);
      setSearchQuery(newValue);
      setPageIndex(0);
      setHasNextPage(true); // Reset search → assume more pages exist
    } else {
      setPopup({
        isOpen: true,
        type: "error",
        message: "Special characters are not allowed. Use only letters, numbers, and spaces.",
      });
    }
  };

  const handlePopupClose = () => {
    setPopup({ isOpen: false, type: "", message: "" });
  };

  const handleKeyDown = (e) => {
    setSearchQuery(e.target.value);
  };

  const getValidatorsForCurrentFile = () => {
    if (!assigningFileId) return [];
    const file = currentFiles.find(f => f.fileId === assigningFileId && f.targetId === targetlanguageId);
    if (!file) return [];
    return validatorsByLanguage[file.targetId] || [];
  };

  const validatorsForAssignment = getValidatorsForCurrentFile();
  console.log(validatorsForAssignment);

  const filteredFiles = useMemo(() => {
    if (!searchQuery) return currentFiles;
    return currentFiles.filter(
      (project) =>
        project.fileName?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [currentFiles, searchQuery]);

  const fileNameClick = (row) => {
    console.log(row);
    const { fileId, targetId } = row.original;
    const file = currentFiles.find(f => f.fileId === fileId && f.targetId === targetId);
    console.log('Clicked file:', file);
    if (!file) return;
    dispatch(setSelectedAssignedFileId(fileId));
    dispatch(setFileMeta({
      fileName: file.fileName
    }));
    dispatch(setFileAndLanguage({
      targetLanguageName: file.targetName,
    }))
    if (file.validatorAdminId) dispatch(setDevAdminId(file.validatorAdminId));
    if (file.sourceId) dispatch(setSourceId(file.sourceId));
    if (file.targetId) dispatch(setTargetId(targetId));
    navigate('/devnagriValidatorAdmin/folders/files/sentences');
  };

  const onStatusClick = (row) => {
    dispatch(setTargetId(row.original.targetId));
    handleAssignButtonClick(row);
  };

  const onSentToCDACClick = async (row) => {
    console.log("Sending file to CDAC:", row);
    const fileId = row.original.fileId;
    const sourceLanguageId = row.original.sourceId;
    const targetLanguageId = row.original.targetId;

    try {
      setIsLoading(true);
      const res = await dispatch(sendToCDAC(fileId, sourceLanguageId, targetLanguageId));
      console.log("[onSentToCDACClick] API response:", res);
      if (res.status === "success") {
        toast.success(
          <div className="p-3 font-serif text-sm font-medium text-white">
            {res.message || "File sent to CDAC successfully"}
          </div>,
          {
            position: "top-right",
            autoClose: 3000,
            theme: "light", // Use 'light' theme to avoid colored theme conflicts
            className: "rounded-lg shadow-md border border-green-200",
            style: {
              backgroundColor: "#10B981", // Green for success
              color: "#ffffff",
              borderRadius: "8px",
            },
          }
        );
        // Auto-refresh the file list after successful send
        await dispatch(fetchInProgressFiles(selectedAssignedFolderId));
      } else {
        toast.error(
          <div className="p-3 font-serif text-sm font-medium text-white">
            {res.message || "Failed to send file to CDAC"}
          </div>,
          {
            position: "top-right",
            autoClose: 3000,
            theme: "light",
            className: "rounded-lg shadow-md border border-red-200",
            style: {
              backgroundColor: "#dc3545", // Red for error
              color: "#ffffff",
              borderRadius: "8px",
            },
          }
        );
      }
    } catch (error) {
      console.error("[onSentToCDACClick] Error:", error);
      toast.error(
        <div className="p-3 font-serif text-sm font-medium text-white">
          {error.message || "Error sending file to CDAC. Please try again."}
        </div>,
        {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
          className: "rounded-lg shadow-md border border-red-200",
          style: {
            backgroundColor: "#dc3545", // Red for error
            color: "#ffffff",
            borderRadius: "8px",
          },
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fileColumns = [
    {
      id: "serial_number",
      header: () => "Sr. No.",
      cell: ({ row, table }) => {
        return pageIndex * pageSize + row.index + 1;
      },
      cellClassName: "text-center",
    },
    {
      id: "fileName",
      header: () => "File Name",
      accessorFn: (row) => row.fileName || "",
      cell: ({ row }) => {
        const fileName = row.original.fileName || "";
        return (
          <span
            className="cursor-pointer text-[#ec7026] hover:text-[#d85d1a] hover:underline"
            onClick={() => fileNameClick(row)}
          >
            {fileName}
          </span>
        );
      },
    },
    {
      id: "sourceLang",
      header: () => "Source Language",
      accessorFn: (row) => row.sourceName,
      cell: ({ row }) => <span>{row.original.sourceName}</span>,
    },
    {
      id: "targetlang",
      header: () => "Target Language",
      accessorFn: (row) => row.targetName,
      cell: ({ row }) => <span>{row.original.targetName}</span>,
    },
    {
      id: "totalSentence",
      header: () => "Total Sentences",
      accessorFn: (row) => row.totalSentences,
      cell: ({ row }) => <span>{row.original.totalSentences}</span>,
    },
    {
      id: "validatedSentenceCount",
      header: () => "Validated Sentences Count",
      accessorFn: (row) => row.validatedSentenceCount,
      cell: ({ row }) => <span>{row.original.validatedSentenceCount}</span>,
    },
    {
      id: "rejectedSentenceCount",
      header: () => "Rejected Sentence Count",
      accessorFn: (row) => row.rejectedSentenceCount,
      cell: ({ row }) => <span>{row.original.rejectedSentenceCount}</span>,
    },
    {
      id: "fStatus",
      header: () => "File Status",
      accessorFn: (row) => row.fStatus || "Pending",
      cell: ({ row }) => {
        const style = getStatusStyle(row.original.devStatus || row.devStatus);
        return (
          <button
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}
          >
            {style.label}
          </button>
        );
      },
    },
    {
      id: "assign",
      header: () => "Assign Validator",
      cell: ({ row }) => {
        return (
          <button
            onClick={() => onStatusClick(row)}
            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200 ${row.original.devStatus === 'ASSIGNED' || row.original.devStatus === 'SUBMITTED_TO_CDAC' || row.original.devStatus === 'COMPLETED'
              ? 'bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed'
              : 'border border-[#0066cc] text-[#0066cc] hover:cursor-pointer hover:bg-[#0066cc] hover:text-white focus:ring-2 focus:ring-[#0066cc]/50'
              }`}
            disabled={row.original.devStatus === 'ASSIGNED' || row.original.devStatus === 'SUBMITTED_TO_CDAC' || row.original.devStatus === 'COMPLETED'}
          >
            {row.original.devStatus === 'ASSIGNED' || row.original.devStatus === 'SUBMITTED_TO_CDAC' || row.original.devStatus === 'COMPLETED' ? `Assigned to ${row.original.devUserName}` : 'Assign'}
          </button>
        );
      },
      cellClassName: "text-center",
    },
    {
      id: "send",
      header: () => "Send to CDAC",
      cell: ({ row }) => {
        return (
          <button
            onClick={() => onSentToCDACClick(row)}
            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200 ${row.original.devStatus !== 'COMPLETED'
              ? 'bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed'
              : 'border border-[#0066cc] text-[#0066cc] hover:cursor-pointer hover:bg-[#0066cc] hover:text-white focus:ring-2 focus:ring-[#0066cc]/50'
              }`}
            disabled={row.original.devStatus !== 'COMPLETED'}
          >
            {row.original.devStatus === 'ASSIGNED' ? 'Not Sent Yet' : row.original.devStatus === 'SUBMITTED_TO_CDAC' || row.original.devStatus === 'COMPLETED' ? 'Sent' : 'Send'}
          </button>
        );
      },
      cellClassName: "text-center",
    }
  ];

  if (!selectedAssignedFolderId) {
    return (
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-gray-500 text-sm font-['Semantic/Strong']">
            Please select a folder to view files.
          </div>
        </div>
      </div>
    );
  }

  if (initialLoading) {
    return (
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-4 w-1/2"></div>
            <div className="h-8 bg-gray-200 rounded mb-4"></div>
            <div className="h-8 bg-gray-200 rounded mb-4"></div>
            <div className="h-8 bg-gray-200 rounded mb-4"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] bg-gray-50/50">
      <div className="lg:pr-8 lg:pt-8 flex justify-end items-center">
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            placeholder="Search Files..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] text-base font-['Semantic/Strong'] text-gray-700 pl-10"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {popup.isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
              {popup.type === "error" ? "Error" : "Info"}
            </h2>
            <p className="mt-2 text-sm text-gray-500 font-['Semantic/Strong']">
              {popup.message}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handlePopupClose}
                className="inline-flex items-center px-4 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      <div className="w-full p-4 sm:p-6 lg:p-8">
        {filteredFiles.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
              <p>No files found in this folder.</p>
              <p className="mt-2">
                If you believe this is an error, please try refreshing the page or contact the administrator.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="w-full overflow-x-auto">
              <GenericTable
                data={[...filteredFiles]}
                columns={fileColumns}
                showPagination={true}
                manualPagination={true}
                pageIndex={pageIndex}
                pageSize={pageSize}
                hasNextPage={hasNextPage}
                onPaginationChange={({ pageIndex, pageSize }) => {
                  setPageIndex(pageIndex);
                  setPageSize(pageSize);
                }}
                emptyMessage="No files available in this folder"
              />
            </div>
          </div>
        )}
        {assignModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-6 animate-fade-in">
              <h2 className="text-lg font-semibold mb-4 text-gray-800">Assign Validator</h2>
              <div className="mb-4">
                <label className="block mb-2 font-medium">Select Validator</label>
                {loadingValidators ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                    <span className="ml-2">Loading validators...</span>
                  </div>
                ) : validatorsForAssignment.length > 0 ? (
                  <select
                    className="border rounded px-2 py-1 w-full"
                    value={selectedValidator[assigningFileId] || ""}
                    onChange={(e) => handleValidatorOptionClick(e.target.value)}
                  >
                    <option value="" disabled>Select Validator</option>
                    {validatorsForAssignment.map((val) => (
                      <option key={val.userId} value={val.userId}>
                        {val.userName} ({val.roleName})
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="text-red-500 text-sm">
                    No validators available for this language (Language ID: {selectedFileTargetLanguageId})
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  className={`bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 ${!selectedValidator[assigningFileId] || validatorsForAssignment.length === 0
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                    }`}
                  disabled={!selectedValidator[assigningFileId] || validatorsForAssignment.length === 0}
                  onClick={async () => {
                    try {
                      console.log(currentFiles);
                      console.log(targetlanguageId);
                      const file = currentFiles.find(f => f.fileId === assigningFileId && f.targetId === targetlanguageId);
                      console.log(file);
                      if (!file) {
                        console.error("File not found");
                        toast.error(
                          <div className="p-3 font-serif text-sm font-medium text-[#d85d1a]">
                            File not found. Please try again.
                          </div>,
                          {
                            position: "top-right",
                            autoClose: 3000,
                            theme: "colored",
                            className: "rounded-lg shadow-md border border-red-200",
                          }
                        );
                        closeModal();
                        return;
                      }

                      console.log(assigningFileId, file.targetId, selectedValidator[assigningFileId]);

                      const result = await dispatch(
                        assignValidatorToFile(
                          assigningFileId,
                          file.targetId,
                          selectedValidator[assigningFileId]
                        )
                      );

                      if (result.success) {
                        const selectedUser = validatorsForAssignment.find(
                          (v) => v.userId == selectedValidator[assigningFileId]
                        );
                        if (selectedUser) {
                          setAssignedValidators((prev) => ({
                            ...prev,
                            [assigningFileId]: `${selectedUser.userName} (${selectedUser.roleName})`,
                          }));
                        }
                        toast.success(
                          <div className="p-3 font-serif text-sm font-medium text-white">
                            {result.message}
                          </div>,
                          {
                            position: "top-right",
                            autoClose: 3000,
                            theme: "colored",
                            className: "rounded-lg shadow-md border border-green-200",
                          }
                        );
                        closeModal();
                        setRefreshingFiles(true);
                        try {
                          await dispatch(fetchInProgressFiles(selectedAssignedFolderId));
                        } finally {
                          setRefreshingFiles(false);
                        }
                      } else {
                        console.log(result);
                        toast.error(
                          <div className="p-3 font-serif text-sm font-medium text-white">
                            Error: {result.message}
                          </div>,
                          {
                            position: "top-right",
                            autoClose: 3000,
                            theme: "colored",
                            className: "rounded-lg shadow-md border border-red-200",
                          }
                        );
                      }
                    } catch (error) {
                      console.error("Error assigning validator:", error);
                      toast.error(
                        <div className="p-3 font-serif text-sm font-medium text-[#d85d1a]">
                          Error assigning validator. Please try again.
                        </div>,
                        {
                          position: "top-right",
                          autoClose: 3000,
                          theme: "colored",
                          className: "rounded-lg shadow-md border border-red-200",
                        }
                      );
                      closeModal();
                    }
                  }}
                >
                  Send to Validator
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileDetailTableDevnagriValidatorAdmin;